import { useEffect, useState } from 'react';
import { useDeskSnapshot, useIsOnline } from '../deskContext';
import { SUCCESS_BAR_MS, syncBarState, type SyncBarTone } from '../syncBarState';

const TONE_CLASSES: Record<SyncBarTone, string> = {
  danger: 'bg-danger text-white',
  offline: 'bg-tint-warning text-ink border-b border-warning',
  syncing: 'bg-tint-sky text-ink border-b border-sky',
  success: 'bg-tint-success text-success border-b border-success',
};

/** The desk sync bar (plan "Offline" UI). The live region is always in the
 * DOM so screen readers announce changes; the bar itself only renders when
 * there is something to say. The danger state has no close button on
 * purpose — it stays until every event in the tray is dealt with. */
export function SyncBar() {
  const snapshot = useDeskSnapshot();
  const online = useIsOnline();
  const [now, setNow] = useState(() => Date.now());

  // Re-render once the 4-second success window ends.
  useEffect(() => {
    if (snapshot.drainedAt === null) return;
    setNow(Date.now());
    const remaining = snapshot.drainedAt + SUCCESS_BAR_MS - Date.now();
    if (remaining <= 0) return;
    const timer = setTimeout(() => setNow(Date.now()), remaining + 50);
    return () => clearTimeout(timer);
  }, [snapshot.drainedAt]);

  const state = snapshot.leader
    ? syncBarState({
        online,
        pending: snapshot.pending.length,
        attention: snapshot.attention.length,
        drainedAt: snapshot.drainedAt,
        now: Math.max(now, snapshot.drainedAt ?? 0),
      })
    : null;

  return (
    <div aria-live="polite" role="status" data-testid="sync-bar">
      {state ? (
        <div className={`flex flex-wrap items-center gap-md px-lg py-xs text-body font-semibold ${TONE_CLASSES[state.tone]}`}>
          <span>{state.text}</span>
          {state.tone === 'danger' ? (
            <a href="/check-in#needs-attention" className="underline">
              Review
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
